import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiSearch, FiFilter, FiGrid, FiList, FiX, FiChevronDown,
  FiHome, FiHeart, FiMapPin, FiPhone, FiMail, FiGlobe,
  FiPackage, FiTruck, FiShield
} from 'react-icons/fi';
import { dealerService } from '../services/dealerService';
import { shedService } from '../services/shedService';
import { useWishlist } from '../context/WishlistContext';

const DealerBrandedPage = () => {
  const { slug } = useParams();
  const [dealer, setDealer] = useState(null);
  const [sheds, setSheds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [filterType, setFilterType] = useState('');
  const { toggleWishlist, isWishlisted } = useWishlist();

  useEffect(() => {
    if (slug) loadDealer();
  }, [slug]);

  const loadDealer = async () => {
    setLoading(true);
    try {
      const { data, error: dealerErr } = await dealerService.getDealerBySlug(slug);
      if (dealerErr || !data) {
        setError('Dealer not found');
        setLoading(false);
        return;
      }
      setDealer(data);

      const shedsResult = await shedService.getShedsByDealer(data.id);
      const available = (shedsResult.data || []).filter(s => {
        const status = (s.status || '').toLowerCase();
        return !status.includes('sold');
      });
      setSheds(available);
    } catch (err) {
      console.error('Error loading dealer page:', err);
      setError('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);

  const getRtoMonthly = (shed) => {
    const base = shed.salePrice && shed.salePrice < shed.price ? shed.salePrice : shed.price;
    return formatPrice(Math.round((base * 1.45) / 36));
  };

  const shedTypes = useMemo(() => {
    const types = [...new Set(sheds.map(s => s.shedType).filter(Boolean))];
    return types.sort();
  }, [sheds]);

  const filteredSheds = useMemo(() => {
    let result = [...sheds];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(s =>
        s.name?.toLowerCase().includes(term) ||
        s.brand?.toLowerCase().includes(term) ||
        s.shedType?.toLowerCase().includes(term) ||
        s.size?.toLowerCase().includes(term)
      );
    }

    if (filterType) {
      result = result.filter(s => s.shedType?.toLowerCase() === filterType.toLowerCase());
    }

    switch (sortBy) {
      case 'price-low': result.sort((a, b) => (a.salePrice || a.price) - (b.salePrice || b.price)); break;
      case 'price-high': result.sort((a, b) => (b.salePrice || b.price) - (a.salePrice || a.price)); break;
      case 'size-large': result.sort((a, b) => (b.squareFeet || 0) - (a.squareFeet || 0)); break;
      default: result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    return result;
  }, [sheds, searchTerm, sortBy, filterType]);

  const locations = useMemo(() => {
    if (!dealer?.dealer_cities) return [];
    return dealer.dealer_cities
      .filter(dc => dc.is_active && dc.city)
      .map(dc => dc.city);
  }, [dealer]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (error || !dealer) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 flex flex-col items-center justify-center">
        <FiPackage className="w-16 h-16 text-gray-300 mb-4" />
        <h2 className="text-2xl font-semibold text-gray-700 mb-2">Dealer Not Found</h2>
        <p className="text-gray-500 mb-6">This dealer page doesn't exist or is no longer active.</p>
        <Link to="/sheds" className="bg-gray-900 text-white px-6 py-3 text-sm font-medium hover:bg-gray-800 transition-colors">
          Browse All Sheds
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{dealer.company_name} Shed Inventory | ShedX</title>
        <meta name="description" content={`Browse sheds from ${dealer.company_name}. ${sheds.length} sheds available with delivery options.`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-20">
        {/* Dealer Hero */}
        <div className="bg-white border-b border-gray-200">
          <div className="container mx-auto px-4 py-10">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Logo */}
              <div className="w-20 h-20 md:w-24 md:h-24 bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                {dealer.logo_url ? (
                  <img src={dealer.logo_url} alt={dealer.company_name} className="w-full h-full object-contain p-2" />
                ) : (
                  <FiHome className="w-10 h-10 text-gray-300" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-2">
                  {dealer.company_name}
                </h1>
                {dealer.description && (
                  <p className="text-gray-500 text-sm leading-relaxed mb-4 max-w-2xl">
                    {dealer.description}
                  </p>
                )}

                {/* Contact row */}
                <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                  {dealer.phone && (
                    <a href={`tel:${dealer.phone}`} className="flex items-center gap-1.5 hover:text-gray-900 transition-colors">
                      <FiPhone className="w-4 h-4" /> {dealer.phone}
                    </a>
                  )}
                  {dealer.email && (
                    <a href={`mailto:${dealer.email}`} className="flex items-center gap-1.5 hover:text-gray-900 transition-colors">
                      <FiMail className="w-4 h-4" /> {dealer.email}
                    </a>
                  )}
                  {dealer.website && (
                    <a href={dealer.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-gray-900 transition-colors">
                      <FiGlobe className="w-4 h-4" /> Website
                    </a>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="flex gap-6 md:gap-8 flex-shrink-0">
                <div className="text-center">
                  <div className="text-2xl font-semibold text-gray-900">{sheds.length}</div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider mt-0.5">Sheds</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-semibold text-gray-900">{locations.length}</div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider mt-0.5">Locations</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Locations */}
        {locations.length > 0 && (
          <div className="bg-white border-b border-gray-200">
            <div className="container mx-auto px-4 py-5">
              <h2 className="text-xs uppercase tracking-wider text-gray-400 mb-3">Locations</h2>
              <div className="flex flex-wrap gap-3">
                {locations.map(city => (
                  <div key={city.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm">
                    <FiMapPin className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <span className="text-gray-900 font-medium">{city.name}</span>
                    <span className="text-gray-400">{city.state_abbr}</span>
                    {city.radius_miles && (
                      <span className="text-gray-400 text-xs ml-1">· {city.radius_miles} mi</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Search + Filters */}
        <div className="container mx-auto px-4 py-8">
          <div className="bg-white border border-gray-200 p-4 mb-8">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search sheds..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 focus:ring-1 focus:ring-gray-400 focus:border-gray-400 outline-none text-sm"
                />
              </div>

              {shedTypes.length > 1 && (
                <div className="relative">
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="appearance-none bg-gray-50 border border-gray-200 px-4 py-3 pr-10 focus:ring-1 focus:ring-gray-400 outline-none text-sm"
                  >
                    <option value="">All Types</option>
                    {shedTypes.map(t => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                  <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              )}

              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none bg-gray-50 border border-gray-200 px-4 py-3 pr-10 focus:ring-1 focus:ring-gray-400 outline-none text-sm"
                >
                  <option value="newest">Newest First</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="size-large">Largest First</option>
                </select>
                <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>

              <div className="flex border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-3 ${viewMode === 'grid' ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                >
                  <FiGrid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-3 ${viewMode === 'list' ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                >
                  <FiList className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Results count */}
          <div className="mb-6 text-gray-600 text-sm">
            Showing <span className="font-semibold text-gray-900">{filteredSheds.length}</span> of {sheds.length} sheds
          </div>

          {/* Inventory Grid */}
          {filteredSheds.length === 0 ? (
            <div className="text-center py-16">
              <FiHome className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Sheds Found</h3>
              <p className="text-gray-500 mb-6">Try adjusting your search or filters.</p>
              {(searchTerm || filterType) && (
                <button
                  onClick={() => { setSearchTerm(''); setFilterType(''); }}
                  className="bg-gray-900 text-white px-6 py-3 hover:bg-gray-800 transition-colors text-sm"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
              {filteredSheds.map((shed) => (
                <div key={shed.id} className="bg-white relative group">
                  <button
                    onClick={(e) => { e.preventDefault(); toggleWishlist(shed.id); }}
                    className="absolute top-3 right-3 z-10 p-1.5 bg-white/80 hover:bg-white transition-colors"
                  >
                    <FiHeart className={`w-4 h-4 ${isWishlisted(shed.id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}`} />
                  </button>

                  <Link to={`/shed/${shed.id}`} className="block">
                    <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
                      <img
                        src={shed.images?.[0] || '/assets/images/placeholder-shed.jpg'}
                        alt={shed.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 left-3 flex gap-1.5">
                        {shed.salePrice && shed.salePrice < shed.price && (
                          <span className="bg-red-600 text-white text-[11px] uppercase tracking-wider font-medium px-2 py-0.5">Sale</span>
                        )}
                      </div>
                      {shed.images?.length > 1 && (
                        <span className="absolute bottom-3 right-3 bg-black/60 text-white text-[11px] px-2 py-0.5">
                          {shed.images.length} photos
                        </span>
                      )}
                    </div>

                    <div className="p-5">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs text-gray-400 uppercase tracking-wider">
                          {shed.shedType} &middot; {shed.size}
                        </p>
                        <span className={`flex items-center gap-1 text-[11px] font-medium ${shed.status === 'available' ? 'text-green-600' : 'text-amber-600'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${shed.status === 'available' ? 'bg-green-500' : 'bg-amber-500'}`} />
                          {shed.status === 'available' ? 'Available' : shed.status}
                        </span>
                      </div>

                      <h3 className="font-medium text-gray-900 mb-1 group-hover:underline">{shed.name}</h3>

                      {shed.lotLocation && (
                        <p className="text-xs text-gray-400 mb-3 flex items-center gap-1">
                          <FiMapPin className="w-3 h-3" /> {shed.lotLocation}
                        </p>
                      )}

                      <div className="flex items-baseline gap-2 mb-1">
                        {shed.salePrice && shed.salePrice < shed.price ? (
                          <>
                            <span className="text-sm text-gray-400 line-through">{formatPrice(shed.price)}</span>
                            <span className="text-lg font-semibold text-red-600">{formatPrice(shed.salePrice)}</span>
                          </>
                        ) : (
                          <span className="text-lg font-semibold text-gray-900">{formatPrice(shed.price)}</span>
                        )}
                        {shed.deliveryIncluded && (
                          <span className="text-xs text-gray-400 ml-auto">Free delivery</span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400 mb-3">
                        or from <span className="text-gray-600 font-medium">{getRtoMonthly(shed)}/mo</span> with Rent-to-Own
                      </p>

                      <div className="flex gap-4 text-[11px] text-gray-500 border-t border-gray-100 pt-3 mt-1">
                        <div><span className="text-gray-400 block">Body</span><span className="text-gray-700">{shed.bodyColor || shed.sidingColor || '—'}</span></div>
                        <div><span className="text-gray-400 block">Roof</span><span className="text-gray-700">{shed.roofColor || '—'}</span></div>
                        <div><span className="text-gray-400 block">Trim</span><span className="text-gray-700">{shed.trimColor || '—'}</span></div>
                        {shed.brand && (
                          <div className="ml-auto text-right"><span className="text-gray-400 block">Built by</span><span className="text-gray-700 font-medium">{shed.brand}</span></div>
                        )}
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            /* List View */
            <div className="space-y-px bg-gray-200 border border-gray-200">
              {filteredSheds.map((shed) => (
                <Link key={shed.id} to={`/shed/${shed.id}`} className="bg-white flex flex-col md:flex-row group">
                  <div className="relative w-full md:w-64 h-48 md:h-auto flex-shrink-0">
                    <img src={shed.images?.[0] || '/assets/images/placeholder-shed.jpg'} alt={shed.name} className="w-full h-full object-cover" />
                    {shed.salePrice && shed.salePrice < shed.price && (
                      <span className="absolute top-3 left-3 bg-red-600 text-white text-[11px] uppercase tracking-wider font-medium px-2 py-0.5">Sale</span>
                    )}
                  </div>
                  <div className="flex-1 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{shed.shedType} &middot; {shed.size}</p>
                      <h3 className="font-medium text-gray-900 mb-1 group-hover:underline">{shed.name}</h3>
                      <div className="flex gap-4 text-[11px] text-gray-500 mt-2">
                        <span>Body: <span className="text-gray-700">{shed.bodyColor || '—'}</span></span>
                        <span>Roof: <span className="text-gray-700">{shed.roofColor || '—'}</span></span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {shed.salePrice && shed.salePrice < shed.price ? (
                        <>
                          <span className="block text-sm text-gray-400 line-through">{formatPrice(shed.price)}</span>
                          <span className="text-lg font-semibold text-red-600">{formatPrice(shed.salePrice)}</span>
                        </>
                      ) : (
                        <span className="text-lg font-semibold text-gray-900">{formatPrice(shed.price)}</span>
                      )}
                      <p className="text-[11px] text-gray-400 mt-0.5">{getRtoMonthly(shed)}/mo RTO</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Powered by ShedX footer */}
        <div className="bg-white border-t border-gray-200 py-6 text-center">
          <Link to="/" className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
            Powered by <span className="font-semibold text-gray-600">ShedX</span>
          </Link>
        </div>
      </div>
    </>
  );
};

export default DealerBrandedPage;
